// frontend/src/components/DegreeAudit/Common/NoMajorDeclared.tsx

import { memo } from 'react';
import { GraduationCap, Sparkles, ArrowRight } from 'lucide-react';
import { dispatchAskChatbotHelp } from '../../../utils/appEvents';
import { isAutoManagedProgram } from '@/utils/programClassification';

interface ProgramRef {
  program?: string;
  [key: string]: unknown;
}

interface AcademicPlanLite {
  majors?: ProgramRef[];
  minors?: ProgramRef[];
  [key: string]: unknown;
}

export interface NoMajorDeclaredProps {
  onOpenManageModal: () => void;
  onManageSemesters?: () => void;
  academicPlan: AcademicPlanLite | null | undefined;
  userName?: string;
  onDismissSetup?: () => void;
}

/**
 * No Major Declared Component — directs users to the AI Assistant for setup help.
 * The AI Assistant handles the full onboarding flow conversationally.
 */
const NoMajorDeclared = memo(
  ({ onOpenManageModal, academicPlan }: NoMajorDeclaredProps) => {
    const hasRealPrograms =
      academicPlan?.majors?.some((m) => !isAutoManagedProgram(m)) ||
      (academicPlan?.minors && academicPlan.minors.length > 0);

    if (hasRealPrograms) {
      return null;
    }

    const handleChatClick = (): void => {
      dispatchAskChatbotHelp({
        message: 'Help me set up my academic plan. What major or program should I add?',
        action: 'setup_major',
      });
    };

    return (
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-4 md:p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full bg-indigo-100 mb-4">
            <GraduationCap className="h-6 w-6 md:h-7 md:w-7 text-indigo-600" />
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Major Declared</h3>

          <p className="text-sm text-gray-600 mb-2">
            Add a program via{' '}
            <button
              onClick={onOpenManageModal}
              className="text-indigo-600 hover:text-indigo-700 font-medium hover:underline"
            >
              Degree Requirements → Manage Programs
            </button>{' '}
            or by chatting with the AI Assistant.
          </p>

          <p className="text-xs text-gray-500 mb-4">
            Not sure yet? Select{' '}
            <span className="font-medium">Letters and Sciences (Exploratory)</span> for an undeclared
            major.
          </p>

          <button
            onClick={handleChatClick}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            <span>Chat with AI Assistant</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }
);

NoMajorDeclared.displayName = 'NoMajorDeclared';

export default NoMajorDeclared;
